
import type { User } from "firebase/auth"
import { useFieldArray } from "react-hook-form"
import type { LiveGenreDocument, LiveGenreId, SpaceKey } from "popopo-msg"
import { api, firestore } from "../../core"
import { Button } from "react-bootstrap"
import { useLiveGenres } from "popopo-lib-front/firestore-access"
import { useToast } from "popopo-lib-front/hook"
import { useZodForm } from "popopo-lib-front/zod-form"
import { z } from "zod/v4"
import { ManagedCheckbox, ManagedFloatingInput, ManagedFloatingSelect } from "popopo-lib-front/component"
import { SpinnerRow } from "../SpinnerRow"

type LiveCreateForms = {
  genreId: string,
  tags: {
    value: string
  }[],
  canEnter: boolean,
}

export const SpaceLiveCreateForm = (props: { firebase: User; spaceKey: SpaceKey }) => {
  const genres = useLiveGenres(firestore)

  if (genres === "loading") {
    return <SpinnerRow />
  }

  return <SpaceLiveCreateFormResolved {...props} genres={genres} />
}

const SpaceLiveCreateFormResolved = (props: { firebase: User; spaceKey: SpaceKey, genres: LiveGenreDocument[] }) => {
  const toast = useToast()

  const form = useZodForm({
    schema: z.object({
      genreId: z.string().min(1, "必須要素です。"),
      tags: z.array(z.object({
        value: z.string().min(1, "必須要素です。"),
      })),
      canEnter: z.boolean(),
    }),
    defaultValues: {
      genreId: props.genres.length === 0 ? "" : props.genres[0].id,
      tags: [{ value: "雑談" }],
      canEnter: true,
    },
    mode: "onBlur"
  })

  const tags = useFieldArray({
    control: form.control,
    name: "tags",
  })

  const onLiveCreate = async (value: LiveCreateForms) => {
    try {
      const idToken = await props.firebase.getIdToken()
      const res = await api.POST("/api/v2/spaces/{space_key}/lives", {
        params: { path: { space_key: props.spaceKey } },
        headers: {
          authorization: `Bearer ${idToken}`,
        },
        body: {
          genreId: value.genreId,
          tags: value.tags.map(x => x.value) ?? [],
          canEnter: value.canEnter,
        },
      })
      if (res.data == null) {
        toast.add({ type: "danger", title: "Liveの作成", message: `作成に失敗しました。${JSON.stringify(res.error)}` })
      } else {
        toast.add({ type: "success", title: "Liveの作成", message: "作成に成功しました。" })
      }
    } catch (e) {
      console.error(e)
      toast.add({ type: "danger", title: "Liveの作成", message: `作成に失敗しました。${e}` })
    }
  }


  const { isSubmitting, isValid } = form.formState

  return (
    <form className="d-flex w-100 flex-column gap-3" onSubmit={form.handleSubmit(onLiveCreate)}>
      <ManagedFloatingSelect control={form.control} label="ジャンル" name="genreId" options={
        [{id: "" as LiveGenreId, label: ""}].concat(props.genres).map((x) => ({
          value: x.id,
          label: x.label,
        }))
      }/>
      <ManagedCheckbox control={form.control} name="canEnter" checkboxLabel="ライブ中のメンバーの入室を許可する"/>

      {tags.fields.map((field, index) => (
        <ManagedFloatingInput key={field.id} label="タグ" control={form.control} name={`tags.${index}.value`} inputGroupAfter={
          <Button type="button" variant="danger" onClick={() => tags.remove(index)}>
            削除
          </Button>
        }/>
      ))}
      <Button type="button" onClick={() => tags.append({ value: new Date().toLocaleString() })}>
        タグ入力欄を追加
      </Button>

      <button type="submit" className="btn btn-primary form-control" disabled={isSubmitting || !isValid}>
        Create Live
      </button>
    </form>
  )
}
